import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError, forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Event, EventType, EventSubtype } from '../../interfaces/old/event.interfaces';
import { environment } from '../../environment/environment';
import { EventDTO } from '../../dto/event/event.dto';
import { OrganizationDTO } from '../../dto/organization/organization.dto';
import { LocationDTO } from '../../dto/location/location.dto';
import { RoomDTO } from '../../dto/location/room.dto';
import { SubTypeDTO } from '../../dto/subtype/subtype.dto';
import { GoalDTO } from '../../dto/goal/goal.dto';
import { ModuleDTO } from '../../dto/module/module.dto';
import { EducationalPathDTO } from '../../dto/educational-path/educational-path.dto';

@Injectable({
  providedIn: 'root'
})
export class EventsService {
  private apiUrl = `${environment.apiUrl}/events`;

  constructor(private http: HttpClient) { }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'An error occurred';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Client Error: ${error.error.message}`;
    } else {
      errorMessage = `Server Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    console.error(errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  // CRUD Operations
  getEvent(id: number): Observable<Event> {
    return this.http.get<Event>(`${this.apiUrl}/${id}`).pipe(
      catchError(this.handleError)
    );
  }

  createEvent(event: Omit<Event, 'id' | 'createdAt' | 'createdBy'>): Observable<Event> {
    return this.http.post<Event>(this.apiUrl, event).pipe(
      catchError(this.handleError)
    );
  }

  updateEvent(id: number, event: Partial<Event>): Observable<Event> {
    return this.http.put<Event>(`${this.apiUrl}/${id}`, event).pipe(
      catchError(this.handleError)
    );
  }

  deleteEvent(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      catchError(this.handleError)
    );
  }

  // Event Type Operations
  getEventTypes(): Observable<EventType[]> {
    return this.http.get<EventType[]>(`${this.apiUrl}/types`).pipe(
      catchError(this.handleError)
    );
  }

  getEventSubtypes(typeId?: number): Observable<EventSubtype[]> {
    const params = typeId ? { typeId: typeId.toString() } : null;
    return this.http.get<EventSubtype[]>(`${this.apiUrl}/subtypes`, params ? { params } : {}).pipe(
      catchError(this.handleError)
    );
  }

  // Resource Management
  assignLocation(eventId: number, locationId: number): Observable<Event> {
    return this.http.post<Event>(`${this.apiUrl}/${eventId}/location`, { locationId }).pipe(
      catchError(this.handleError)
    );
  }

  assignMaterials(eventId: number, materials: any[]): Observable<Event> {
    return this.http.post<Event>(`${this.apiUrl}/${eventId}/materials`, { materials }).pipe(
      catchError(this.handleError)
    );
  }

  assignProvisions(eventId: number, provisions: any[]): Observable<Event> {
    return this.http.post<Event>(`${this.apiUrl}/${eventId}/provisions`, { provisions }).pipe(
      catchError(this.handleError)
    );
  }

  // Document Management
  attachDocuments(eventId: number, documentIds: number[]): Observable<Event> {
    return this.http.post<Event>(`${this.apiUrl}/${eventId}/documents`, { documentIds }).pipe(
      catchError(this.handleError)
    );
  }

  removeDocument(eventId: number, documentId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${eventId}/documents/${documentId}`).pipe(
      catchError(this.handleError)
    );
  }

  // Status Management
  publishEvent(eventId: number): Observable<Event> {
    return this.http.post<Event>(`${this.apiUrl}/${eventId}/publish`, {}).pipe(
      catchError(this.handleError)
    );
  }

  cancelEvent(eventId: number, reason?: string): Observable<Event> {
    return this.http.post<Event>(`${this.apiUrl}/${eventId}/cancel`, { reason }).pipe(
      catchError(this.handleError)
    );
  }

  completeEvent(eventId: number): Observable<Event> {
    return this.http.post<Event>(`${this.apiUrl}/${eventId}/complete`, {}).pipe(
      catchError(this.handleError)
    );
  }

  // New method to get event with populated data
  getEventAndPopulate(eventId: number): Observable<EventDTO> {
    return this.http.get<EventDTO>(`${this.apiUrl}/${eventId}`).pipe(
      switchMap(event => {
        // Create requests for organization, location, and room
        const requests: {[key: string]: Observable<any>} = {};
        
        // Only fetch organization if not already populated
        if (event.organizations_id && !event.organization) {
          requests['organization'] = this.http.get<OrganizationDTO>(`${environment.apiUrl}/organizations/${event.organizations_id}`);
        }

        // Add location and room requests if they exist
        if (event.predefined_location_guid && !event.predefined_location) {
          requests['predefinedLocation'] = this.http.get<LocationDTO>(`${environment.apiUrl}/locations/by-guid/${event.predefined_location_guid}`);
        }
        
        if (event.predefined_room_guid && !event.predefined_room) {
          requests['predefinedRoom'] = this.http.get<RoomDTO>(`${environment.apiUrl}/rooms/by-guid/${event.predefined_room_guid}`);
        }

        // Process items based on their type
        if (event.items && event.items.length > 0) {
          const itemRequests = event.items.map(item => {
            let itemDetailsUrl = '';
            switch (item.item_type) {
              case 'subtypes':
                itemDetailsUrl = `${environment.apiUrl}/subtypes/${item.item_id}`;
                return this.http.get<SubTypeDTO>(itemDetailsUrl).pipe(
                  map(details => {
                    return {
                      ...item,
                      item_details: details
                    };
                  }),
                  catchError(() => of({
                    ...item,
                    item_details: null
                  }))
                );
              case 'goals':
                itemDetailsUrl = `${environment.apiUrl}/goals/${item.item_id}`;
                return this.http.get<GoalDTO>(itemDetailsUrl).pipe(
                  map(details => {
                    return {
                      ...item,
                      item_details: details
                    };
                  }),
                  catchError(() => of({
                    ...item,
                    item_details: null
                  }))
                );
              case 'modules':
                itemDetailsUrl = `${environment.apiUrl}/modules/${item.item_id}`;
                return this.http.get<ModuleDTO>(itemDetailsUrl).pipe(
                  map(details => {
                    return {
                      ...item,
                      item_details: details
                    };
                  }),
                  catchError(() => of({
                    ...item,
                    item_details: null
                  }))
                );
              case 'educationalpaths':
                itemDetailsUrl = `${environment.apiUrl}/educational-paths/${item.item_id}`;
                return this.http.get<EducationalPathDTO>(itemDetailsUrl).pipe(
                  map(details => {
                    return {
                      ...item,
                      item_details: details
                    };
                  }),
                  catchError(() => of({
                    ...item,
                    item_details: null
                  }))
                );
              default:
                return of(item);
            }
          });

          // Add items to the requests
          requests['items'] = forkJoin(itemRequests);
        }

        // If no requests, return the event as is
        if (Object.keys(requests).length === 0) {
          return of(event);
        }

        // Process all requests and update the event
        return forkJoin(requests).pipe(
          map(results => {
            const updatedEvent = { ...event };
            
            // Update organization if fetched
            if (results['organization']) {
              updatedEvent.organization = results['organization'];
            }
            
            // Update location if fetched
            if (results['predefinedLocation']) {
              updatedEvent.predefined_location = results['predefinedLocation'];
            }
            
            // Update room if fetched
            if (results['predefinedRoom']) {
              updatedEvent.predefined_room = results['predefinedRoom'];
            }
            
            // Update items if fetched
            if (results['items']) {
              updatedEvent.items = results['items'];
            }
            
            return updatedEvent;
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  // Add a method to get all events with populated data
  getAllPopulatedEvents(): Observable<EventDTO[]> {
    return this.http.get<EventDTO[]>(this.apiUrl).pipe(
      switchMap(events => {
        if (events.length === 0) {
          return of([]);
        }
        
        // Process each event to populate its data
        const populatedEvents$ = events.map(event => this.getEventAndPopulate(event.event_id));
        return forkJoin(populatedEvents$);
      }),
      catchError(this.handleError)
    );
  }

  // Get event by GUID
  getEventByGuid(guid: string): Observable<EventDTO> {
    return this.http.get<EventDTO>(`${this.apiUrl}/by-guid/${guid}`).pipe(
      catchError(this.handleError)
    );
  }

  // Get sub-events by parent event ID
  getSubEventsByParentId(parentEventId: number): Observable<EventDTO[]> {
    return this.http.get<EventDTO[]>(`${this.apiUrl}/sub-events/${parentEventId}`).pipe(
      catchError(this.handleError)
    );
  }
}
